import React from 'react';
import { Shield, Mail, Lock, Eye, Database, UserCheck } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-12 h-12" />
            <h1 className="text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-xl text-blue-100">
            Last Updated: November 15, 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">

          {/* Introduction */}
          <section>
            <p className="text-lg text-gray-700 leading-relaxed">
              At BuildYourDevOps ("we", "our", or "us"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
            </div>

            <div className="space-y-4 ml-9">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Information</h3>
                <p className="text-gray-700 leading-relaxed">
                  We may collect personal information that you provide to us, including but not limited to:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1 text-gray-700">
                  <li>Name and contact information (email address, phone number)</li>
                  <li>Company information and job title</li>
                  <li>Login credentials (username and encrypted password)</li>
                  <li>Payment and billing information</li>
                  <li>Communication preferences</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">LinkedIn Integration Data</h3>
                <p className="text-gray-700 leading-relaxed">
                  When you connect your LinkedIn account to our LinkedIn Content Automation service, we collect:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1 text-gray-700">
                  <li>LinkedIn profile information (name, email, profile URL)</li>
                  <li>OAuth access tokens (encrypted and stored securely)</li>
                  <li>Post content and analytics data</li>
                  <li>Keywords and automation preferences</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Automatically Collected Information</h3>
                <p className="text-gray-700 leading-relaxed">
                  We automatically collect certain information when you visit our website:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1 text-gray-700">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
            </div>

            <div className="ml-9 space-y-2">
              <p className="text-gray-700 leading-relaxed">We use the collected information for the following purposes:</p>
              <ul className="list-disc ml-6 space-y-1 text-gray-700">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send administrative information, updates, and security alerts</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Automate LinkedIn content posting on your behalf (with your explicit authorization)</li>
                <li>Generate AI-powered content based on your preferences</li>
                <li>Monitor and analyze usage patterns to improve user experience</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations and enforce our terms of service</li>
              </ul>
            </div>
          </section>

          {/* Data Sharing */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">How We Share Your Information</h2>
            </div>

            <div className="ml-9 space-y-3">
              <p className="text-gray-700 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>

              <div>
                <h3 className="font-semibold text-gray-800">Service Providers</h3>
                <p className="text-gray-700">
                  We work with trusted third-party service providers who assist us in operating our website and services:
                </p>
                <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700">
                  <li><strong>LinkedIn API</strong>: For posting content to your LinkedIn profile</li>
                  <li><strong>Anthropic (Claude AI)</strong>: For AI-powered content generation</li>
                  <li><strong>Freepik</strong>: For image search and suggestions</li>
                  <li><strong>AWS SES</strong>: For sending transactional emails</li>
                  <li><strong>Payment processors</strong>: For handling billing and payments</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800">Legal Requirements</h3>
                <p className="text-gray-700">
                  We may disclose your information if required by law or in response to valid requests by public authorities (e.g., court orders, government agencies).
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800">Business Transfers</h3>
                <p className="text-gray-700">
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
                </p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-red-600" />
              <h2 className="text-2xl font-bold text-gray-900">Data Security</h2>
            </div>

            <div className="ml-9 space-y-3">
              <p className="text-gray-700 leading-relaxed">
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="list-disc ml-6 space-y-1 text-gray-700">
                <li>SSL/TLS encryption for all data transmission</li>
                <li>Encrypted storage of sensitive data (passwords, OAuth tokens)</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Restricted access to personal information on a need-to-know basis</li>
                <li>Secure authentication using JWT tokens with expiration</li>
                <li>PostgreSQL prepared statements to prevent SQL injection</li>
                <li>Rate limiting to prevent abuse</li>
              </ul>
              <p className="text-gray-600 italic mt-3">
                While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-orange-600" />
              <h2 className="text-2xl font-bold text-gray-900">Data Retention</h2>
            </div>

            <div className="ml-9">
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. When you delete your account or disconnect your LinkedIn integration, we will delete or anonymize your personal data within 30 days, except for data we are required to retain for legal, accounting, or security purposes.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Your Rights</h2>
            </div>

            <div className="ml-9">
              <p className="text-gray-700 leading-relaxed mb-2">You have the following rights regarding your personal information:</p>
              <ul className="list-disc ml-6 space-y-1 text-gray-700">
                <li><strong>Access</strong>: Request a copy of the personal information we hold about you</li>
                <li><strong>Correction</strong>: Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion</strong>: Request deletion of your personal information (right to be forgotten)</li>
                <li><strong>Data Portability</strong>: Request a copy of your data in a machine-readable format</li>
                <li><strong>Withdraw Consent</strong>: Disconnect your LinkedIn account or opt-out of communications at any time</li>
                <li><strong>Object</strong>: Object to processing of your information for certain purposes</li>
              </ul>
              <p className="text-gray-700 mt-3">
                To exercise these rights, please contact us at <a href="mailto:privacy@buildyourdevops.com" className="text-blue-600 hover:underline font-medium">privacy@buildyourdevops.com</a>
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-teal-600" />
              <h2 className="text-2xl font-bold text-gray-900">Cookies and Tracking</h2>
            </div>

            <div className="ml-9">
              <p className="text-gray-700 leading-relaxed mb-2">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc ml-6 space-y-1 text-gray-700">
                <li><strong>Essential Cookies</strong>: Required for authentication and security</li>
                <li><strong>Functional Cookies</strong>: Remember your preferences and settings</li>
                <li><strong>Analytics Cookies</strong>: Help us understand how users interact with our website</li>
              </ul>
              <p className="text-gray-700 mt-3">
                You can control cookies through your browser settings. Note that disabling cookies may affect website functionality.
              </p>
            </div>
          </section>

          {/* Third-Party Links */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-pink-600" />
              <h2 className="text-2xl font-bold text-gray-900">Third-Party Links</h2>
            </div>

            <div className="ml-9">
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites (LinkedIn, Freepik, etc.). We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
              </p>
            </div>
          </section>

          {/* Children's Privacy */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-cyan-600" />
              <h2 className="text-2xl font-bold text-gray-900">Children's Privacy</h2>
            </div>

            <div className="ml-9">
              <p className="text-gray-700 leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete that information.
              </p>
            </div>
          </section>

          {/* International Users */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-gray-600" />
              <h2 className="text-2xl font-bold text-gray-900">International Data Transfers</h2>
            </div>

            <div className="ml-9">
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure that such transfers comply with applicable data protection laws and that your information receives adequate protection.
              </p>
            </div>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-yellow-600" />
              <h2 className="text-2xl font-bold text-gray-900">Changes to This Privacy Policy</h2>
            </div>

            <div className="ml-9">
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="border-t-2 border-gray-200 pt-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
            </div>

            <div className="ml-9 bg-blue-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> <a href="mailto:privacy@buildyourdevops.com" className="text-blue-600 hover:underline">privacy@buildyourdevops.com</a></p>
                <p><strong>Support Email:</strong> <a href="mailto:support@buildyourdevops.com" className="text-blue-600 hover:underline">support@buildyourdevops.com</a></p>
                <p><strong>Website:</strong> <a href="https://buildyourdevops.com" className="text-blue-600 hover:underline">https://buildyourdevops.com</a></p>
              </div>
            </div>
          </section>

          {/* Footer Note */}
          <section className="bg-gray-100 rounded-xl p-6 text-center">
            <p className="text-sm text-gray-600">
              By using BuildYourDevOps services, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
